import classNames from "classnames";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { Divider } from "components/design/Divider";
import { TableUI } from "components/design/TableUI";
import ToolTip from "components/design/toolTip";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { AnimatedNumbers } from "components/DesignSystem/AnimatedNumbers/AnimatedNumbers";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { InfoItem } from "components/DesignSystem/InfoItem/InfoItem";
import { Link } from "components/DesignSystem/Link/Link";
import Loader from "components/DesignSystem/Loader/loader";
import Modal from "components/DesignSystem/Modal/Modal";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import Radio from "components/DesignSystem/RadioGroup/RadioGroup";
import { Search } from "components/DesignSystem/Search/Search";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { Info } from "components/icons/Info";
import { MagnifyingGlass } from "components/icons/MagnifyingGlass";
import {
  DiscountTransactions,
  initialTransaction,
} from "components/LinkInkleInvoiceModal/LinkInkleInvoiceModal";
import AIGenerateButtonStar from "static/images/AIGenerateButtonStar.svg";
import { RadioCard } from "components/RadioCard/RadioCard";
import { TransactionsBankSelector } from "components/Transaction/AddTransaction/TransactionsBankSelector";
import { TxnFilter } from "components/Transaction/Filter/Filter";
import { MerchantComponent } from "components/Transaction/MerchantSelector";
import TransactionColumn from "components/Transaction/TransactionColumn";
import { USD } from "constants/currency";
import { DISPLAY_DATE, YYYY_MM_DD } from "constants/date";
import {
  PAY_PARTIALLY,
  RESOLVE_DIFFERENCE,
  WRITE_OFF_REMAINING,
} from "constants/invoiceResolveOptions";
import dayjs from "dayjs";
import { Form, Formik, useFormikContext } from "formik";
import { linkInkleSchema } from "formValidations/linkInvoiceSchema";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useUpdateQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import { useTransactionsFilter } from "hooks/useTransactionsFilter";
import randomBytes from "randombytes";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  createColumnHelper,
  getCoreRowModel,
  Row,
  useReactTable,
} from "react-table-8.10.7";
import {
  useGetEntityInvoiceQuery,
  useMarkInvoicePaidMutation,
} from "store/apis/invoices";
import {
  useGetAllTransactionsDataQuery,
  useGetMappedInvoiceQuery,
} from "store/apis/transactions";
import {
  useGetBillOrRequestQuery,
  useMarkBillPaidMutation,
} from "store/apis/vendorBills";
import { resetTxnFilters } from "store/slices/transactionFilter";
import { RootState } from "store/store";
import { Transaction, Transactions } from "types/Models/books";
import { Invoice, SuggestedTransaction } from "types/Models/invoice";
import { VendorBill } from "types/Models/vendorBills";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import { currency } from "utils/Currency";
import { debounce } from "utils/debouncing";
import { formatDate } from "utils/formatDate";
import { pluralize } from "utils/pluralize";
import TicketResolve from "static/images/TicketResolve.svg";
import { ArrowLightLine } from "components/icons/ArrowLightLine";
import {
  BILL_STATUS,
  billStatusType,
  WITHHOLDING_STATUS,
} from "constants/vendorBills";
import { INVOICE_STATUSES, invoiceStatusType } from "constants/invoicing";
import { CustomExchangeRateModal } from "components/CustomExchangeRate/CustomExchangeRateModal";
import { EditPencil } from "components/icons/EditPencil";
import { CreditNote } from "store/apis/creditNotes";
import { CreditNotesTabContent } from "./CreditNotesTabContent";
import { WriteOffRemainingOption } from "./WriteOffRemainingOption";

const getInvoicePendingAmount = (invoice: Invoice) => {
  if (!invoice) return 0;

  if (invoice.pending_amount) {
    return Number(invoice.pending_amount);
  }

  if (invoice.currency === USD) {
    return Number(invoice.due_balance);
  }
  return Number(invoice.payment_record_amount?.amount_in_base_currency);
};

// Any linking transactions should be linked here should be negative
// -100 = (-60 selected from transaction list) + (-40 entered manually)
const getBillPendingAmount = (bill: VendorBill) => {
  if (!bill) return 0;

  if (bill.pending_amount) {
    return -Number(bill.pending_amount);
  }

  if (bill.currency === USD) {
    return -Number(bill.due_balance);
  }

  return -Number(bill.payment_record_amount?.amount_in_base_currency);
};

export const PendingTooltip = ({
  pendingAmount,
  amount,
  transactionId,
}: {
  pendingAmount: number;
  amount: number;
  transactionId: string;
}) => {
  const entityId = useCurrentEntityId();
  const { data: linkedInvoices, isLoading: isLinkedTxnDataLoading } =
    useGetMappedInvoiceQuery(
      {
        entityId,
        transactionId,
      },
      { skip: !entityId || !transactionId }
    );

  return (
    <span className="t-text-text-30 t-text-body-sm t-flex t-gap-0.5 t-flex-wrap t-items-center t-justify-end">
      <AmountSuperScript amount={pendingAmount} /> of
      {<AmountSuperScript amount={amount} />} yet to be linked
      <ToolTip
        text={
          linkedInvoices?.invoices
            ?.filter((i) => i.amount_used)
            ?.map(
              (i) =>
                `${currency({ amount: i.amount_used! })} linked to ${
                  i.invoice_number
                }`
            )
            .join(",") || "No invoices linked yet"
        }
      >
        <span>
          <Info size="14" />
        </span>
      </ToolTip>
    </span>
  );
};

const LinkTransactionBody = ({
  type = "INVOICE",
  invoiceDate,
  invoiceId,
  status,
  exchangeRate,
  selectedTransactions,
  setSelectedTransactions,
  selectedCreditNotes,
  setSelectedCreditNotes,
  resolveDifferece,
  openSuggestedMatches = false,
}: {
  type?: "BILL" | "INVOICE";
  invoiceDate?: string | null;
  invoiceId: string;
  status?: invoiceStatusType;
  exchangeRate?: number;
  selectedTransactions: Transactions[];
  setSelectedTransactions: React.Dispatch<React.SetStateAction<Transactions[]>>;
  selectedCreditNotes: CreditNote[];
  setSelectedCreditNotes: React.Dispatch<React.SetStateAction<CreditNote[]>>;
  resolveDifferece: boolean;
  openSuggestedMatches?: boolean;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { link } = useConstructInternalLink();
  const [currentPage, setCurrentPage] = useState(1);
  const [suggestedAccordionValue, setSuggestedAccordionValue] = useState<
    string | undefined
  >(openSuggestedMatches ? "SUGGESTED" : undefined);

  const addTransactionModal = useModal();
  const exchangeRateModal = useModal();

  const { data: invoice } = useGetEntityInvoiceQuery(
    {
      entityId,
      invoiceId,
      groupId,
    },
    {
      skip: type !== "INVOICE",
    }
  );

  const { data: bill } = useGetBillOrRequestQuery(
    { groupId, entityId, billId: invoiceId },
    {
      skip: !invoiceId || !entityId || !groupId || type !== "BILL",
    }
  );

  const billDueBalance =
    bill?.bill_payment_status === BILL_STATUS.PARTIALLY_PAID ||
    bill?.withholding_status === WITHHOLDING_STATUS.WITHHELD
      ? bill?.pending_amount
      : bill?.due_balance;
  const invoiceDueBalance =
    invoice?.status === INVOICE_STATUSES.PARTIALLY_PAID
      ? invoice?.pending_amount
      : invoice?.due_balance;
  const dueBalance = billDueBalance || invoiceDueBalance;
  const usdDueBalance =
    bill?.payment_record_amount?.amount_in_base_currency ||
    invoice?.payment_record_amount?.amount_in_base_currency;
  const currency = bill?.currency || invoice?.currency;

  const {
    filters: {
      uncategorised,
      minAmount,
      maxAmount,
      vendors,
      categoryIds,
      ...restFilter
    },
  } = useSelector((state: RootState) => state.transactionFilter);

  const [sorting, setSorting] = useState<
    {
      desc: boolean;
      id: "AMOUNT" | "DATE" | "VENDOR";
    }[]
  >([
    {
      id: "DATE",
      desc: true,
    },
  ]);

  const sortCol = sorting[0]?.id;
  const sortOrder = sorting[0]?.desc;

  const { values: formikValues, setFieldValue } = useFormikContext<{
    min_amount?: string;
    max_amount?: string;
    search?: string;
    invoice_number?: number;
    due_balance?: number;
    pay_partially?: boolean;
    resolve_difference?: boolean;
    write_off_remaining?: boolean;
    transactions: { merchant: ""; category: ""; description: ""; amount: 0 }[];
  }>();

  const allFilters = useTransactionsFilter({
    txnAccountType: "ALL",
    pageSize: "20",
    searchTerm: formikValues?.search || null,
    sortCol: sortCol,
    sortedToDec: sortOrder,
    pageNum: currentPage,
    transactionIds: "",
    messageId: "",
  });

  let cashFlow = "CREDIT";

  if (bill) {
    cashFlow = "DEBIT";
  }

  const {
    data: transactionData,
    isLoading,
    isSuccess,
  } = useGetAllTransactionsDataQuery(
    {
      ...allFilters,
      cashFlow: { value: cashFlow },
      excludeFullyApplied: true,
    },
    {
      skip: !groupId || !entityId,
      refetchOnMountOrArgChange: true,
    }
  );

  const { transactions = [] } = transactionData || {};

  const suggestedTransactions =
    invoice?.suggested_txns || bill?.suggested_txns || [];

  const paginationData = {
    totalPage: transactionData?.total_pages!,
    currentPage: transactionData?.current_page!,
    itemsPerPage: transactionData?.per_page!,
    totalItemCount: transactionData?.total_count!,
  };

  const { updateMultiple } = useUpdateQuery();

  const dispatch = useAppDispatch();

  useEffect(() => {
    updateMultiple([
      {
        query: "startDate",
        value: dayjs(invoice?.invoice_date || bill?.bill_date).format(
          YYYY_MM_DD
        ),
      },
      {
        query: "endDate",
        value: dayjs().format(YYYY_MM_DD),
      },
    ]);

    return () => {
      dispatch(resetTxnFilters());
    };
  }, [invoice?.invoice_date, bill?.bill_date]);

  const columnHelper = createColumnHelper<Transactions>();
  const suggestedColumnHelper = createColumnHelper<SuggestedTransaction>();

  const onRowClick = (row: Row<Transactions>) => {
    if (!row.getCanSelect()) return;
    const isRowSelected = selectedTransactions.find(
      (txn) => txn.transaction.uuid === row.original.transaction.uuid
    );

    if (isRowSelected) {
      return setSelectedTransactions((prev) =>
        prev.filter(
          (txn) => txn.transaction.uuid !== row.original.transaction.uuid
        )
      );
    }
    setSelectedTransactions((prev) => [...prev, row.original]);
  };

  const onSuggestedRowClick = (row: Row<SuggestedTransaction>) => {
    if (!row.getCanSelect()) return;
    const isRowSelected = selectedTransactions.find(
      (txn) => txn.transaction.uuid === row.original.transaction_uuid
    );

    if (isRowSelected) {
      return setSelectedTransactions((prev) =>
        prev.filter(
          (txn) => txn.transaction.uuid !== row.original.transaction_uuid
        )
      );
    }

    const convertedTransaction: Transactions = {
      transaction: {
        uuid: row.original.transaction_uuid,
        amount: row.original.amount,
        date: row.original.date,
        merchant: row.original.merchant.name,
        merchant_uuid: row.original.merchant.uuid,
        logo: row.original.merchant.logo,
        description: row.original.description,
      } as Transaction,
      available_amount: row.original.amount,
    } as Transactions;

    setSelectedTransactions((prev) => [...prev, convertedTransaction]);
  };

  const transactionAmount = selectedTransactions.reduce(
    (acc, txn) =>
      acc +
      (txn.has_linked_invoices ? txn.available_amount : txn.transaction.amount),
    0
  );

  const creditNotesAmount = selectedCreditNotes.reduce(
    (acc, cn) => acc + cn.remaining_balance,
    0
  );

  const resolvingTransactions = formikValues.transactions.reduce(
    (acc, txn) => acc + (txn.amount || 0),
    0
  );

  const pendingAmountInSource =
    (invoice ? getInvoicePendingAmount(invoice) : 0) ||
    (bill ? getBillPendingAmount(bill) : 0);

  const pendingAmount =
    Number(pendingAmountInSource) -
    (transactionAmount + resolvingTransactions + creditNotesAmount);

  const pendingAmountWithoutManual =
    Number(pendingAmountInSource) - (transactionAmount + creditNotesAmount);

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "select",
        size: 2,
        enableSorting: false,
        header: () => <></>,

        cell: ({ row }) => {
          const isDisabled = !row.getCanSelect();

          return (
            <ConditionalToolTip
              condition={
                isDisabled &&
                "The total amount of selected transactions has already exceeded the invoice amount"
              }
            >
              <span>
                <Checkbox
                  {...{
                    checked: row.getIsSelected(),
                    disabled: isDisabled,
                    indeterminate: row.getIsSomeSelected(),
                    key: row.id,
                  }}
                />
              </span>
            </ConditionalToolTip>
          );
        },
      }),

      columnHelper.accessor("transaction.date", {
        id: "DATE",
        size: 11,
        header: "Date",
        cell: (info) => (
          <div className="t-text-subtext t-text-text-30">
            {dayjs(info.getValue()).format(DISPLAY_DATE)}
          </div>
        ),
      }),

      columnHelper.accessor("transaction.from", {
        id: "logo",
        size: 5,
        enableSorting: false,
        header: () => (
          <div className="t-flex t-justify-center t-w-full">Source</div>
        ),
        cell: (info) => {
          const transaction = info.row.original.transaction;

          return <TransactionColumn.Source transaction={transaction} />;
        },
      }),

      columnHelper.accessor("transaction.merchant", {
        id: "VENDOR",
        size: 15,
        header: "Vendor",
        cell: (info) => {
          const {
            row: {
              original: {
                transaction: { logo, merchant },
              },
            },
          } = info;

          if (!merchant) {
            return "-";
          }

          return <MerchantComponent name={merchant} logo={logo} />;
        },
      }),

      columnHelper.accessor("transaction.category", {
        id: "category",
        size: 20,
        header: "Category",
        enableSorting: false,
        cell: (info) => {
          const {
            row: {
              original: {
                transaction: { category },
              },
            },
          } = info;

          if (!category) {
            return "-";
          }

          return <>{category?.name}</>;
        },
      }),

      columnHelper.accessor("transaction.description", {
        id: "description",
        size: 21,
        header: "Description",
        enableSorting: false,
        cell: (info) => {
          const {
            row: {
              original: {
                transaction: { description },
              },
            },
          } = info;

          if (!description) {
            return "-";
          }

          return <>{description}</>;
        },
      }),

      columnHelper.accessor("transaction.amount", {
        id: "AMOUNT",
        size: 26,
        header: () => (
          <span className="t-flex t-justify-end t-ml-auto">Amount</span>
        ),
        cell: (info) => {
          const amount = info.getValue();
          const { available_amount: pending_amount } = info.row.original || {};

          return (
            <div className="t-text-right">
              <TransactionColumn.Amount amount={amount} />
              {Boolean(pending_amount) && pendingAmount !== amount && (
                <PendingTooltip
                  pendingAmount={pending_amount}
                  amount={amount}
                  transactionId={info.row.original.transaction.uuid}
                />
              )}
            </div>
          );
        },
      }),
    ],
    [pendingAmount]
  );

  const suggestedColumns = useMemo(
    () => [
      suggestedColumnHelper.display({
        id: "select",
        size: 2,
        enableSorting: false,
        header: () => <></>,

        cell: ({ row }) => {
          const isDisabled = !row.getCanSelect();

          return (
            <ConditionalToolTip
              condition={
                isDisabled &&
                "The total amount of selected transactions has already exceeded the invoice amount"
              }
            >
              <span>
                <Checkbox
                  {...{
                    checked: row.getIsSelected(),
                    disabled: isDisabled,
                    indeterminate: row.getIsSomeSelected(),
                    key: row.id,
                  }}
                />
              </span>
            </ConditionalToolTip>
          );
        },
      }),

      suggestedColumnHelper.accessor("date", {
        id: "DATE",
        size: 11,
        header: "Date",
        enableSorting: false,
        cell: (info) => (
          <div className="t-text-subtext t-text-text-30">
            {dayjs(info.getValue()).format(DISPLAY_DATE)}
          </div>
        ),
      }),

      suggestedColumnHelper.accessor("source", {
        id: "logo",
        size: 5,
        enableSorting: false,
        header: () => (
          <div className="t-flex t-justify-center t-w-full">Source</div>
        ),
        cell: () => {
          return <TransactionColumn.Source transaction={{} as any} />;
        },
      }),

      suggestedColumnHelper.accessor("merchant", {
        id: "VENDOR",
        size: 15,
        header: "Vendor",
        enableSorting: false,
        cell: (info) => {
          const merchant = info.getValue();

          if (!merchant?.name) {
            return "-";
          }

          return (
            <MerchantComponent name={merchant.name} logo={merchant.logo} />
          );
        },
      }),

      suggestedColumnHelper.accessor("description", {
        id: "description",
        size: 21,
        header: "Description",
        enableSorting: false,
        cell: (info) => {
          const description = info.getValue();

          if (!description) {
            return "-";
          }

          return <>{description}</>;
        },
      }),

      suggestedColumnHelper.accessor("match_score", {
        id: "match_percentage",
        size: 20,
        header: "Match percentage",
        enableSorting: false,
        cell: (info) => {
          const matchScore = info.getValue() || 0;
          const tagType = matchScore > 80 ? "green" : "yellow";

          return (
            <Tag tagType={tagType} size="small" rounded icon={false}>
              {Math.round(matchScore)}%
            </Tag>
          );
        },
      }),

      suggestedColumnHelper.accessor("amount", {
        id: "AMOUNT",
        size: 26,
        enableSorting: false,
        header: () => (
          <span className="t-flex t-justify-end t-ml-auto">Amount</span>
        ),
        cell: (info) => {
          const amount = info.getValue();

          return (
            <div className="t-text-right">
              <TransactionColumn.Amount amount={amount} />
            </div>
          );
        },
      }),
    ],
    [pendingAmount]
  );

  let enableSelection = true;

  if (invoice) {
    enableSelection = pendingAmount > 0;
  }

  if (bill) {
    enableSelection = pendingAmount < 0;
  }

  const selectedRows = selectedTransactions.reduce((acc, txn) => {
    acc[txn.transaction.uuid] = true;
    return acc;
  }, {} as Record<string, boolean>);

  const table = useReactTable({
    data: transactions || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: (row) => {
      return row.getIsSelected() ? true : enableSelection;
    },
    manualSorting: true,
    state: {
      rowSelection: selectedRows,
      sorting,
    },
    //@ts-ignore
    onSortingChange: setSorting,
    getRowId: (row) => row.transaction.uuid,
    enableMultiRowSelection: true,
    enableMultiSort: false,
    enableSortingRemoval: false,
    defaultColumn: {
      size: 10,
      minSize: 1,
      maxSize: 100,
    },
  });

  const suggestedTable = useReactTable({
    data: suggestedTransactions || [],
    columns: suggestedColumns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: (row) => {
      return row.getIsSelected() ? true : enableSelection;
    },
    manualSorting: true,
    state: {
      rowSelection: selectedRows,
      sorting,
    },
    //@ts-ignore
    onSortingChange: setSorting,
    getRowId: (row) => row.transaction_uuid,
    enableMultiRowSelection: true,
    enableMultiSort: false,
    enableSortingRemoval: false,
    defaultColumn: {
      size: 10,
      minSize: 1,
      maxSize: 100,
    },
  });

  const handleSearch = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFieldValue("search", value);
  });

  if (isLoading && !isSuccess) {
    return (
      <div className="t-w-full t-h-full t-flex t-justify-center t-align-middle">
        <Loader size="small" />
      </div>
    );
  }

  const resource = type === "INVOICE" ? "Invoice" : "Bill";

  let resolveValue;

  if (formikValues.resolve_difference) {
    resolveValue = RESOLVE_DIFFERENCE;
  }

  if (formikValues.pay_partially) {
    resolveValue = PAY_PARTIALLY;
  }

  if (formikValues.write_off_remaining) {
    resolveValue = WRITE_OFF_REMAINING;
  }

  const isMultiCurrency = currency !== USD;
  const exchangeGainOrLoss =
    pendingAmountWithoutManual > 0 ? "Exchange Loss" : "Exchange Gain";

  const isDifferenceExpense = pendingAmountWithoutManual > 0;
  const incomeOrExpense = isDifferenceExpense ? "Expense" : "Income";
  const differenceLabel = isDifferenceExpense ? "an expense" : "income";
  const differenceCategory = isDifferenceExpense ? "expense" : "income";

  const onResolveValueChange = (value: string) => {
    setFieldValue("resolve_difference", value === RESOLVE_DIFFERENCE);
    setFieldValue("pay_partially", value === PAY_PARTIALLY);
    setFieldValue("write_off_remaining", value === WRITE_OFF_REMAINING);

    const selectedTransactionDate = selectedTransactions[0]?.transaction.date;
    const transactionDate = selectedTransactionDate
      ? dayjs(selectedTransactionDate).format(DISPLAY_DATE)
      : "";

    if (value === PAY_PARTIALLY || value === WRITE_OFF_REMAINING) {
      setFieldValue("transactions", [
        {
          ...initialTransaction,
          id: randomBytes(10).toString("hex"),
          date: transactionDate,
        },
      ]);
    }

    if (value === RESOLVE_DIFFERENCE) {
      setFieldValue("transactions", [
        {
          ...initialTransaction,
          id: randomBytes(10).toString("hex"),
          date: transactionDate,
          description: isMultiCurrency ? exchangeGainOrLoss : incomeOrExpense,
          amount: pendingAmount,
        },
      ]);
    }
  };

  const overpayment =
    type === "INVOICE"
      ? pendingAmountWithoutManual < 0
      : pendingAmountWithoutManual > 0;

  const isWriteOffAllowed = type === "INVOICE" && !overpayment;
  const differenceAmount = Math.abs(pendingAmountWithoutManual);

  const originalRate = invoice?.payment_record_amount?.original_exchange_rate
    ? invoice?.payment_record_amount?.original_exchange_rate
    : exchangeRate;

  const tooltipText = invoice?.has_exchange_rate_changed ? (
    <div className="t-text-left">
      Custom exchange rate applied
      <br />
      1.00 USD = {exchangeRate} {invoice?.currency}
    </div>
  ) : (
    <div className="t-text-left">
      Exchange rate is calculated based on the invoice date.
      <br />
      (1.00 USD = {exchangeRate} {invoice?.currency} on{" "}
      {formatDate(invoice?.invoice_date)})
    </div>
  );

  return (
    <div className="t-flex t-flex-col t-gap-4 t-text-text-60 t-h-full">
      <div className="t-border-solid t-border t-border-neutral-0 t-py-4 t-px-5 t-bg-white t-flex t-gap-8 t-rounded-lg sm:t-grid sm:t-grid-cols-2 sm:t-gap-3 sm:t-px-3">
        <InfoItem label={`${resource} date`}>
          <span className="t-font-semibold">{formatDate(invoiceDate)}</span>
        </InfoItem>

        <Divider orientation="vertical" classNames="sm:t-hidden" />

        <InfoItem label={`${resource} number`}>
          <Link
            to={link(
              invoice
                ? `/books/invoices-and-customers/${invoiceId}`
                : `/books/bills-and-vendors/bill/${invoiceId}`
            )}
            target="_blank"
          >
            {invoice?.invoice_number || bill?.bill_number}
          </Link>
        </InfoItem>

        {(invoice?.customer || bill?.entity_merchant_data) && (
          <>
            <Divider orientation="vertical" classNames="sm:t-hidden" />
            <InfoItem label={invoice ? "To" : "Vendor"}>
              <span className="t-font-semibold t-capitalize">
                {invoice?.customer?.company_name ||
                  bill?.entity_merchant_data?.name}
              </span>{" "}
              <span className="t-text-neutral-20">•</span>{" "}
              <span className="t-text-text-30">
                {invoice?.customer?.email || bill?.entity_merchant_data?.email}
              </span>
            </InfoItem>
          </>
        )}

        <Divider orientation="vertical" classNames="sm:t-hidden" />

        <div
          className={classNames(
            "t-ml-auto t-flex t-gap-8 sm:t-ml-0 sm:t-justify-start sm:t-gap-6",
            { "sm:t-col-span-2": isMultiCurrency }
          )}
        >
          <InfoItem
            label={
              <div className="t-text-body-sm t-text-text-30 t-flex t-justify-end sm:t-justify-start t-gap-1 t-items-center">
                <span>{`Pending ${resource} amount`}</span>
                {invoice?.linked_credit_debit_notes &&
                  invoice.linked_credit_debit_notes.length > 0 && (
                    <ToolTip
                      side="bottom"
                      text={
                        <div className="t-text-left t-flex t-flex-col t-gap-0.5">
                          <div className="t-flex t-justify-between t-gap-4">
                            <span>Invoice:</span>
                            <AmountSuperScript
                              amount={Number(invoice.invoice_amount || 0)}
                              currencyCode={invoice.currency}
                            />
                          </div>
                          {invoice.linked_credit_debit_notes.map((note) => (
                            <div
                              key={note.uuid}
                              className="t-flex t-justify-between t-gap-4"
                            >
                              <span>Credit note {note.note_id}:</span>
                              <span>
                                -
                                <AmountSuperScript
                                  amount={note.linked_amount}
                                  currencyCode={invoice.currency}
                                />
                              </span>
                            </div>
                          ))}
                        </div>
                      }
                    >
                      <span className="t-flex t-items-center">
                        <Info size="14" stroke="1.4" />
                      </span>
                    </ToolTip>
                  )}
              </div>
            }
          >
            <div className="t-text-right sm:t-text-left t-font-semibold">
              <AmountSuperScript
                amount={Number(dueBalance || 0)}
                currencyCode={currency}
              />
            </div>
          </InfoItem>

          {isMultiCurrency && (
            <>
              <Divider orientation="vertical" classNames="sm:t-hidden" />
              <InfoItem
                label={
                  <div className="t-text-body-sm t-text-text-30  t-flex t-justify-end t-gap-1 t-items-center">
                    <span>USD Equivalent</span>
                    <ToolTip text={tooltipText}>
                      <span className="t-flex t-items-center">
                        <Info size="14" stroke="1.4" />
                      </span>
                    </ToolTip>
                  </div>
                }
              >
                <div className="t-text-right">
                  <AmountSuperScript amount={Math.abs(usdDueBalance || 0)} />
                  {(status === INVOICE_STATUSES.DUE ||
                    status === INVOICE_STATUSES.OVERDUE) && (
                    <ToolTip
                      text="Set Custom Exchange rate"
                      side="bottom"
                      sideOffset={8}
                    >
                      <span className="t-pl-1">
                        <Button
                          customType="ghost_icon"
                          size="small"
                          onClick={exchangeRateModal.open}
                        >
                          <EditPencil />
                        </Button>
                      </span>
                    </ToolTip>
                  )}
                </div>
              </InfoItem>
              {exchangeRateModal.isOpen && (
                <CustomExchangeRateModal
                  isOpen={exchangeRateModal.isOpen}
                  close={exchangeRateModal.close}
                  invoiceId={invoiceId}
                  currency={currency!}
                  currentRate={exchangeRate || 0}
                  originalRate={originalRate}
                />
              )}
            </>
          )}
        </div>
      </div>
      <div className="t-flex t-flex-col t-gap-4">
        {!resolveDifferece ? (
          <>
            {suggestedTransactions.length > 0 && (
              <Accordion.Root
                key={suggestedAccordionValue}
                type="single"
                collapsible
                value={suggestedAccordionValue}
                onValueChange={setSuggestedAccordionValue}
                className="t-space-y-4"
              >
                <Accordion.Item value="SUGGESTED">
                  <Accordion.Trigger
                    arrowPosition="left"
                    className="!t-text-body-sm !t-font-medium"
                  >
                    <div className="t-flex t-items-center t-justify-between t-w-full">
                      <div className="t-flex t-items-center t-gap-2">
                        Suggested transaction matches
                        <Tag tagType="purple" size="small" rounded icon={false}>
                          <img
                            src={AIGenerateButtonStar}
                            alt="AI"
                            className="t-w-3 t-h-3 t-mr-1"
                          />
                          AI
                        </Tag>
                      </div>
                      <span className="t-flex t-items-center t-gap-1 t-text-body-sm t-text-text-30">
                        <img
                          src={TicketResolve}
                          alt="TicketResolve"
                          className="t-size-3"
                        />
                        {pluralize(
                          suggestedTransactions.length,
                          "match",
                          "matches"
                        )}{" "}
                        found
                      </span>
                    </div>
                  </Accordion.Trigger>
                  <Accordion.Content>
                    <div className="t-flex t-flex-col t-gap-4">
                      {suggestedTransactions.length === 0 ? (
                        <div className="t-text-text-30 t-text-body-sm">
                          No suggested matches found
                        </div>
                      ) : (
                        <TableUI
                          height="auto"
                          table={suggestedTable}
                          enableSort
                          size="regular"
                          onRowClick={onSuggestedRowClick}
                        />
                      )}
                    </div>
                  </Accordion.Content>
                </Accordion.Item>
              </Accordion.Root>
            )}

            {suggestedAccordionValue === "SUGGESTED" && (
              <div className="t-flex t-justify-start">
                <Button
                  customType="link_neutral"
                  size="small"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSuggestedAccordionValue(undefined);
                  }}
                >
                  View all transactions
                  <ArrowLightLine />
                </Button>
              </div>
            )}

            <div className="t-flex t-justify-between">
              <p className="t-text-body-sm t-font-medium t-text-text-100 t-m-0">
                All Transactions
              </p>
              <Button onClick={() => addTransactionModal.open()} size="small">
                + Add transaction
              </Button>

              <TransactionsBankSelector
                isOpen={addTransactionModal.isOpen}
                close={addTransactionModal.close}
                onBack={addTransactionModal.close}
              />
            </div>
            <div className="t-flex t-justify-between sm:t-flex-col sm:t-gap-2">
              <div className="t-flex t-gap-2 t-w-full t-flex-wrap">
                <Search
                  className="t-w-[12%] focus:t-w-2/6 t-transition-all sm:t-w-auto sm:t-min-w-0 sm:t-flex-1 sm:focus:t-w-auto"
                  onChange={handleSearch}
                  placeholder="Search"
                />
                <TxnFilter />
              </div>
              <div className="t-ml-auto sm:t-ml-0 sm:t-self-end">
                <PaginationHandler
                  {...paginationData}
                  per_page={paginationData.itemsPerPage || 1}
                  total_count={paginationData.totalItemCount || 0}
                  total_pages={paginationData?.totalPage || 1}
                  onPageNumChange={setCurrentPage}
                  page_num={currentPage}
                />
              </div>
            </div>
            <Form>
              {transactions.length === 0 ? (
                <div className="t-w-full t-h-full t-flex t-flex-col t-justify-center t-items-center t-py-12 t-gap-2 t-text-i-neutral-10">
                  <MagnifyingGlass size="149" />
                  <div className="t-w-full t-h-full t-flex t-flex-col t-justify-center t-items-center t-gap-3">
                    <div className="t-text-body-sm t-text-text-30">
                      No transactions found
                    </div>
                  </div>
                </div>
              ) : (
                <TableUI
                  height="300px"
                  table={table}
                  enableSort
                  size="regular"
                  onRowClick={onRowClick}
                />
              )}
            </Form>
            {type === "INVOICE" && invoice?.customer_id && (
              <>
                <Divider />
                <CreditNotesTabContent
                  customerId={invoice.customer_id}
                  selectedCreditNotes={selectedCreditNotes}
                  setSelectedCreditNotes={setSelectedCreditNotes}
                  pendingAmount={pendingAmount}
                />
              </>
            )}
            <div className="t-sticky t-bottom-0 t-bg-white t-text-body-sm t-font-medium t-flex t-justify-end t-gap-4 t-text-text-60 t-border-t t-border-neutral-0 t-z-10 t-p-3">
              <span>Amount pending:</span>
              <span>
                <AnimatedNumbers amount={pendingAmount} />
              </span>
            </div>
          </>
        ) : (
          <Form>
            <p className="t-m-0 t-text-body-sm t-mb-6 t-text-text-100">
              The {resource.toLowerCase()} being marked paid is of{" "}
              <span className="t-font-semibold">
                <AmountSuperScript
                  amount={
                    currency === USD
                      ? Number(dueBalance)
                      : Math.abs(usdDueBalance || 0)
                  }
                  currencyCode={USD}
                />
              </span>
              {isMultiCurrency && (
                <span className="t-font-semibold">
                  {" "}
                  (
                  <AmountSuperScript
                    amount={Number(dueBalance || 0)}
                    currencyCode={currency}
                  />
                  )
                </span>
              )}
              {overpayment ? (
                <span>
                  , which is{" "}
                  <span className="t-font-semibold">
                    <AmountSuperScript
                      amount={Math.abs(pendingAmountWithoutManual)}
                      currencyCode={USD}
                    />{" "}
                  </span>
                  less than the selected transaction(s)
                </span>
              ) : (
                <span>
                  , which is{" "}
                  <span className="t-font-semibold">
                    <AmountSuperScript
                      amount={Math.abs(pendingAmountWithoutManual)}
                      currencyCode={USD}
                    />{" "}
                  </span>
                  more than the total of the selected transactions
                </span>
              )}
              {isMultiCurrency && (
                <span>
                  . This difference may be due to exchange rate changes
                </span>
              )}
              {overpayment
                ? ". How would you like to handle the difference?"
                : ". How would you like to handle the extra amount?"}
            </p>

            <Radio.Root
              value={resolveValue}
              name="resolve_difference"
              onValueChange={onResolveValueChange}
            >
              <Radio.Content className="t-flex t-flex-col t-gap-5">
                <RadioCard value={RESOLVE_DIFFERENCE}>
                  <div className="t-text-body-sm t-flex t-flex-col t-gap-0.5">
                    <span>Categorise as {differenceCategory}</span>
                    <p className="t-text-text-30 t-m-0 t-italic t-text-body-italic">
                      Marks the {resource.toLowerCase()} Paid and records the{" "}
                      <AmountSuperScript
                        amount={differenceAmount}
                        currencyCode={USD}
                      />{" "}
                      difference as{" "}
                      {isMultiCurrency
                        ? `an exchange gain/loss or ${differenceCategory}`
                        : differenceLabel}
                      .
                    </p>
                  </div>
                </RadioCard>
                {formikValues.resolve_difference && (
                  <div className="t-border-solid t-border-neutral-10 t-border t-pt-1 t-p-3 t-rounded-md">
                    <DiscountTransactions
                      pendingAmount={pendingAmount}
                      invoiceId={invoiceId}
                      type={invoice ? "INVOICE" : "BILL"}
                    />
                  </div>
                )}
                <RadioCard value={PAY_PARTIALLY}>
                  <div className="t-text-body-sm t-flex t-flex-col t-gap-0.5">
                    {overpayment ? (
                      <span>
                        Partially link to transaction(s)
                        <p className="t-text-text-30 t-m-0 t-italic t-text-body-italic">
                          Apply the {resource.toLowerCase()} amount only. The
                          remaining{" "}
                          <span>
                            <AmountSuperScript
                              amount={Math.abs(pendingAmountWithoutManual)}
                              currencyCode={USD}
                            />{" "}
                          </span>{" "}
                          from the transaction(s) will stay unallocated as '
                          {type === "INVOICE"
                            ? "Unapplied Cash Payment Income"
                            : "Unapplied Cash Bill Payment Expense"}
                          '.
                        </p>
                      </span>
                    ) : (
                      <span>
                        Mark {resource.toLowerCase()} partially paid
                        <p className="t-text-text-30 t-m-0 t-italic t-text-body-italic">
                          Applies the payment only.{" "}
                          <span>
                            <AmountSuperScript
                              amount={Math.abs(pendingAmountWithoutManual)}
                              currencyCode={USD}
                            />{" "}
                          </span>{" "}
                          {isMultiCurrency && (
                            <span>
                              {" "}
                              (
                              <AmountSuperScript
                                amount={
                                  exchangeRate
                                    ? Math.abs(pendingAmountWithoutManual) *
                                      exchangeRate
                                    : Number(dueBalance || 0)
                                }
                                currencyCode={currency}
                              />
                              ){" "}
                            </span>
                          )}
                          stays due on the {resource.toLowerCase()}.
                        </p>
                      </span>
                    )}
                  </div>
                </RadioCard>
                {isWriteOffAllowed && (
                  <WriteOffRemainingOption
                    amount={differenceAmount}
                    isSelected={Boolean(formikValues.write_off_remaining)}
                    invoiceDate={invoice?.invoice_date}
                  />
                )}
              </Radio.Content>
            </Radio.Root>
          </Form>
        )}
      </div>
    </div>
  );
};

export const LinkInvoiceToTransactionBody = ({
  isOpen,
  close,
  invoiceId,
  type = "INVOICE",
  openSuggestedMatches = false,
}: ModalProps & {
  invoiceId: string;
  type?: "BILL" | "INVOICE";
  openSuggestedMatches?: boolean;
}) => {
  const { alertToast, successToast } = useToast();
  const entityId = useCurrentEntityId();
  const groupId = useCurrentGroupContext().uuid;
  const [markInvoicePaid, { isLoading }] = useMarkInvoicePaidMutation();
  const [markBillPaid, { isLoading: isBillLoading }] =
    useMarkBillPaidMutation();
  const [selectedTransactions, setSelectedTransactions] = useState<
    Transactions[]
  >([]);
  const [selectedCreditNotes, setSelectedCreditNotes] = useState<CreditNote[]>(
    []
  );

  const resolveDiffereceStep = useModal();

  const { data: invoiceData, isLoading: isInvoiceLoading } =
    useGetEntityInvoiceQuery(
      {
        entityId,
        invoiceId,
        groupId,
      },
      {
        skip: type !== "INVOICE",
      }
    );

  const { data: bill } = useGetBillOrRequestQuery(
    { groupId, entityId, billId: invoiceId },
    {
      skip: !invoiceId || !entityId || !groupId || type !== "BILL",
    }
  );

  const date = bill?.bill_date || invoiceData?.invoice_date;

  const exchangeRate = invoiceData?.payment_record_amount?.exchange_rate;

  const status = invoiceData?.status;

  const initialTransactions = useMemo(
    () =>
      Array.from({ length: 1 }).map((arr, i) => ({
        id: randomBytes(10).toString("hex"),
        ...initialTransaction,
      })),
    []
  );

  const initialValues = {
    transactions: initialTransactions,
    resolve_difference: false,
    pay_partially: false,
    write_off_remaining: false,
    write_off_date: dayjs().format(YYYY_MM_DD),
  };

  const onSubmit = async (values: {
    transactions: typeof initialTransactions;
    resolve_difference: Boolean;
    pay_partially: Boolean;
    write_off_remaining: boolean;
    write_off_date: string;
  }) => {
    try {
      const transactions = values.resolve_difference
        ? values.transactions.map(
            ({ amount, description, category, merchant }) => {
              return {
                amount: amount,
                description: description,
                category_id: category,
                merchant_id: merchant,
              };
            }
          )
        : null;

      const pendingAmount =
        type === "INVOICE"
          ? getInvoicePendingAmount(invoiceData!)
          : getBillPendingAmount(bill!);

      const selectedTransactionsTotal = selectedTransactions.reduce(
        (acc, txn) =>
          acc +
          (txn.has_linked_invoices
            ? txn.available_amount
            : txn.transaction.amount),
        0
      );

      const totalResolvingAmount = transactions?.reduce(
        (acc, txn) => acc + (txn.amount || 0),
        0
      );

      const creditNotesTotal = selectedCreditNotes.reduce(
        (acc, cn) => acc + cn.remaining_balance,
        0
      );

      const transactionAmount =
        Math.abs(selectedTransactionsTotal) +
        creditNotesTotal +
        (transactions ? Math.abs(totalResolvingAmount || 0) : 0);

      const partiallyPaying = Math.abs(pendingAmount) > transactionAmount;
      const overpayment = Math.abs(pendingAmount) < transactionAmount;
      const isCreditNoteOverpayment =
        overpayment && selectedCreditNotes.length > 0;

      if (
        (overpayment || partiallyPaying) &&
        !isCreditNoteOverpayment &&
        !resolveDiffereceStep.isOpen
      ) {
        return resolveDiffereceStep.open();
      }

      if (type === "BILL") {
        await markBillPaid({
          entityId,
          invoiceId,
          transactionIds: selectedTransactions.map(
            (txn) => txn.transaction.uuid
          ),
          recordType: type,
          ...(transactions && { addedTransactions: transactions }),
        }).unwrap();
      } else {
        await markInvoicePaid({
          entityId,
          invoiceId: invoiceId,
          transactionIds: selectedTransactions.map(
            (txn) => txn.transaction.uuid
          ),
          creditNoteIds: selectedCreditNotes.map((cn) => cn.uuid),
          ...(transactions && { addedTransactions: transactions }),
          ...(values.write_off_remaining && {
            writeOffRemainingAt: values.write_off_date,
          }),
        }).unwrap();
      }

      let successMessage = "Payment recorded";

      if (values.pay_partially || values.resolve_difference) {
        successMessage = "Partial payment recorded";
      }

      if (values.write_off_remaining) {
        successMessage = "Payment recorded and remaining amount written off";
      }

      successToast({ message: successMessage });
      close();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <Formik onSubmit={onSubmit} initialValues={initialValues}>
      {({ isSubmitting, submitForm, values, setFieldValue }) => {
        const resolved = values.resolve_difference
          ? linkInkleSchema.isValidSync(values)
          : true;

        const hasSelectedItems =
          selectedTransactions.length > 0 || selectedCreditNotes.length > 0;

        const hasWriteOffDate =
          !values.write_off_remaining || Boolean(values.write_off_date);

        const valid = !resolveDiffereceStep.isOpen
          ? hasSelectedItems
          : hasSelectedItems && resolved && hasWriteOffDate;

        const disabled =
          isSubmitting ||
          !valid ||
          isLoading ||
          (resolveDiffereceStep.isOpen &&
            !values.pay_partially &&
            !values.resolve_difference &&
            !values.write_off_remaining);

        return (
          <Modal.Root open={isOpen} onOpenChange={close}>
            <Modal.Content
              size="xxxl"
              useCustomOverlay
              onClick={(e) => e.stopPropagation()}
            >
              <Modal.Header>
                <Modal.Title>Record payment</Modal.Title>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body asChild>
                <div className="t-overflow-auto t-px-8 t-py-5 sm:t-px-3">
                  {isInvoiceLoading ? (
                    <div className="t-w-full t-h-full t-flex t-justify-center t-align-middle">
                      <Loader size="small" />
                    </div>
                  ) : (
                    <LinkTransactionBody
                      selectedTransactions={selectedTransactions}
                      setSelectedTransactions={setSelectedTransactions}
                      status={status!}
                      exchangeRate={exchangeRate!}
                      selectedCreditNotes={selectedCreditNotes}
                      setSelectedCreditNotes={setSelectedCreditNotes}
                      type={type}
                      invoiceDate={date}
                      invoiceId={invoiceId}
                      resolveDifferece={resolveDiffereceStep.isOpen}
                      openSuggestedMatches={openSuggestedMatches}
                    />
                  )}
                </div>
              </Modal.Body>
              <Modal.FooterButtonGroup>
                {resolveDiffereceStep.isOpen ? (
                  <Button
                    customType="secondary"
                    onClick={() => {
                      resolveDiffereceStep.close();
                      setFieldValue("transactions", initialTransactions);
                      setFieldValue("resolve_difference", false);
                      setFieldValue("pay_partially", false);
                      setFieldValue("write_off_remaining", false);
                    }}
                  >
                    Back
                  </Button>
                ) : (
                  <Modal.RawClose asChild>
                    <Button>Cancel</Button>
                  </Modal.RawClose>
                )}

                <Button
                  type="button"
                  customType="primary"
                  isLoading={isSubmitting || isLoading}
                  disabled={disabled}
                  onClick={submitForm}
                >
                  Proceed
                </Button>
              </Modal.FooterButtonGroup>
            </Modal.Content>
          </Modal.Root>
        );
      }}
    </Formik>
  );
};

export const LinkInvoiceToTransactionModal = ({
  isOpen,
  close,
  invoiceId,
  type = "INVOICE",
  openSuggestedMatches = false,
}: ModalProps & {
  invoiceId: string;
  type?: "BILL" | "INVOICE";
  openSuggestedMatches?: boolean;
}) => {
  if (!isOpen) return null;

  return (
    <LinkInvoiceToTransactionBody
      isOpen={isOpen}
      close={close}
      invoiceId={invoiceId}
      type={type}
      openSuggestedMatches={openSuggestedMatches}
    />
  );
};
